import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { AlertCircle, EthernetPort, Network, Printer } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  createImpressora,
  deleteImpressora,
  fetchImpressoras,
  fetchUnidades,
  impressoraKeys,
  updateImpressora,
} from '@/services/impressoraService';
import type { IImpressora, IImpressoraFormData } from '@/types/impressora';
import { EMPTY_IMPRESSORA_FORM } from '@/types/impressora';
import './impressoras.css';
export const Route = createFileRoute('/_authenticated/configuracao/impressoras/')({
  component: ImpressorasPage,
});

const impressoraSchema = z.object({
  nome: z
    .string()
    .trim()
    .min(1, { message: 'Informe o nome da impressora.' }),
  ip: z
    .string()
    .trim()
    .min(1, { message: 'Informe o IP.' })
    .regex(
      /^(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}$/,
      { message: 'IP inválido.' },
    ),
  porta: z
    .number({ error: 'Informe a porta.' })
    .int({ message: 'Porta deve ser inteira.' })
    .min(1, { message: 'Porta minima: 1.' })
    .max(65535, { message: 'Porta maxima: 65535.' }),
});
type TImpressoraSchema = z.infer<typeof impressoraSchema>;

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string | { message?: string } } | undefined;
    if (typeof data?.error === 'string') {
      return data.error;
    }
    if (data?.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

function ImpressorasPage() {
  const queryClient = useQueryClient();

  const [manualUnidadeId, setManualUnidadeId] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IImpressora | null>(null);
  const [formError, setFormError] = useState('');
  const [deleting, setDeleting] = useState<IImpressora | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { data: unidades = [], isLoading: unidadesLoading } = useQuery({
    queryKey: impressoraKeys.unidades(),
    queryFn: fetchUnidades,
  });

  const selectedUnidadeId = manualUnidadeId || unidades[0]?.id || '';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TImpressoraSchema>({
    resolver: zodResolver(impressoraSchema),
    defaultValues: EMPTY_IMPRESSORA_FORM,
  });

  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: impressoraKeys.list(selectedUnidadeId),
    queryFn: () => fetchImpressoras(selectedUnidadeId),
    enabled: Boolean(selectedUnidadeId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: impressoraKeys.all });

  const createMutation = useMutation({
    mutationFn: createImpressora,
    onSuccess: () => {
      void invalidate();
      closeModal();
      setSuccessMessage('Impressora cadastrada.');
    },
    onError: (err) => setFormError(extractApiError(err, 'Falha ao cadastrar impressora.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: IImpressoraFormData }) =>
      updateImpressora(id, payload),
    onSuccess: () => {
      void invalidate();
      closeModal();
      setSuccessMessage('Impressora atualizada.');
    },
    onError: (err) => setFormError(extractApiError(err, 'Falha ao atualizar impressora.')),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteImpressora,
    onSuccess: () => {
      void invalidate();
      setDeleting(null);
      setSuccessMessage('Impressora excluída.');
    },
  });
  const mutationPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const openCreateModal = () => {
    setEditing(null);
    setFormError('');
    setSuccessMessage('');
    reset(EMPTY_IMPRESSORA_FORM);
    setModalOpen(true);
  };

  const openEditModal = (impressora: IImpressora) => {
    setEditing(impressora);
    setFormError('');
    setSuccessMessage('');
    reset({
      nome: impressora.nome,
      ip: impressora.ip,
      porta: impressora.porta,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormError('');
    reset(EMPTY_IMPRESSORA_FORM);
  };

  const onSubmit = (formData: TImpressoraSchema) => {
    setFormError('');
    if (!selectedUnidadeId) {
      setFormError('Selecione uma unidade.');
      return;
    }

    if (editing) {
      updateMutation.mutate({ id: editing.id, payload: formData });
      return;
    }

    createMutation.mutate({
      unidade_id: selectedUnidadeId,
      ...formData,
    });
  };

  return (
    <div className="impressoras-page">
      <div className="impressoras-page__header">
        <p className="impressoras-page__subtitle">
          Cadastre e gerencie impressoras de rede por unidade.
        </p>
        <button
          type="button"
          className="impressoras-page__btn-primary"
          disabled={!selectedUnidadeId || mutationPending}
          onClick={openCreateModal}
        >
          Nova impressora
        </button>
      </div>

      <div className="impressoras-page__filters">
        <label htmlFor="unidade-select">Unidade</label>
        <select
          id="unidade-select"
          value={selectedUnidadeId}
          onChange={(event) => setManualUnidadeId(event.target.value)}
        >          {unidades.length === 0 && (
            <option value="">Nenhuma unidade disponível</option>
          )}
          {unidades.map((unidade) => (
            <option
              key={unidade.id}
              value={unidade.id}
            >
              {unidade.nome}
            </option>
          ))}
        </select>
      </div>

      {successMessage && (
        <p
          className="impressoras-page__success"
          role="status"
          aria-live="polite"
        >
          {successMessage}
        </p>
      )}
      {isError && (
        <p className="impressoras-page__error">
          {extractApiError(error, 'Falha ao carregar impressoras.')}
        </p>
      )}
      {deleteMutation.isError && (
        <p className="impressoras-page__error">Falha ao excluir impressora.</p>
      )}

      <div className="impressoras-page__table-container">
        <Table>
          <caption className="sr-only">Impressoras cadastradas</caption>
          <Table.Header>
            <Table.Row>
              <Table.Head scope="col">Nome</Table.Head>
              <Table.Head scope="col">IP</Table.Head>
              <Table.Head scope="col">Porta</Table.Head>
              <Table.Head scope="col">Criado por</Table.Head>
              <Table.Head scope="col">Criado em</Table.Head>
              <Table.Head scope="col">Ações</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Content>
            {!selectedUnidadeId && !unidadesLoading && (
              <Table.Row>
                <Table.Cell
                  colSpan={6}
                  className="impressoras-page__empty"
                >
                  Selecione uma unidade para listar impressoras.
                </Table.Cell>
              </Table.Row>
            )}
            {selectedUnidadeId && isLoading && (
              <Table.Row>
                <Table.Cell
                  colSpan={6}
                  className="impressoras-page__empty"
                >
                  Carregando…
                </Table.Cell>
              </Table.Row>
            )}
            {selectedUnidadeId &&
              !isLoading &&
              data.map((impressora) => (
                <Table.Row key={impressora.id}>
                  <Table.Cell>{impressora.nome}</Table.Cell>
                  <Table.Cell>{impressora.ip}</Table.Cell>
                  <Table.Cell>{impressora.porta}</Table.Cell>
                  <Table.Cell>{impressora.criado_por}</Table.Cell>
                  <Table.Cell>
                    {new Date(impressora.created_at).toLocaleString('pt-BR')}
                  </Table.Cell>
                  <Table.Cell className="impressoras-page__actions">
                    <button
                      type="button"
                      disabled={mutationPending}
                      onClick={() => openEditModal(impressora)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      disabled={mutationPending}
                      onClick={() => {
                        setSuccessMessage('');
                        setDeleting(impressora);
                      }}
                    >
                      Excluir
                    </button>
                  </Table.Cell>
                </Table.Row>
              ))}
            {selectedUnidadeId && !isLoading && !isError && data.length === 0 && (
              <Table.Row>
                <Table.Cell
                  colSpan={6}
                  className="impressoras-page__empty"
                >
                  Nenhuma impressora cadastrada.
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Content>
        </Table>
      </div>

      {modalOpen && (
        <Modal
          onClose={closeModal}
          className="impressoras-page__modal"
        >
          <Modal.Header title={editing ? 'Editar impressora' : 'Nova impressora'} />
          <Modal.Content>
            <form
              id="impressora-form"
              className="impressoras-page__form"
              onSubmit={(event) => {
                void handleSubmit(onSubmit)(event);
              }}
            >
              <div className="impressoras-page__field-group">
                <label
                  className="impressoras-page__label"
                  htmlFor="nome"
                >
                  Nome
                </label>
                <div className={`impressoras-page__field${errors.nome ? ' impressoras-page__field--error' : ''}`}>
                  <Printer
                    className="impressoras-page__field-icon"
                    size={18}
                    aria-hidden="true"
                  />
                  <input
                    id="nome"
                    type="text"
                    autoComplete="off"
                    placeholder="Nome da impressora"
                    {...register('nome')}
                  />
                </div>
                {errors.nome?.message && (
                  <p className="impressoras-page__field-error">
                    <AlertCircle
                      className="impressoras-page__field-icon impressoras-page__field-icon--alert"
                      size={14}
                      aria-hidden="true"
                    />
                    {errors.nome.message}
                  </p>
                )}
              </div>

              <div className="impressoras-page__field-group">
                <label
                  className="impressoras-page__label"
                  htmlFor="ip"
                >
                  IP
                </label>
                <div className={`impressoras-page__field${errors.ip ? ' impressoras-page__field--error' : ''}`}>
                  <Network
                    className="impressoras-page__field-icon"
                    size={18}
                    aria-hidden="true"
                  />
                  <input
                    id="ip"
                    type="text"
                    autoComplete="off"
                    placeholder="192.168.1.100"
                    {...register('ip')}
                  />
                </div>
                {errors.ip?.message && (
                  <p className="impressoras-page__field-error">
                    <AlertCircle
                      className="impressoras-page__field-icon impressoras-page__field-icon--alert"
                      size={14}
                      aria-hidden="true"
                    />
                    {errors.ip.message}
                  </p>
                )}
              </div>

              <div className="impressoras-page__field-group">
                <label
                  className="impressoras-page__label"
                  htmlFor="porta"
                >
                  Porta
                </label>
                <div className={`impressoras-page__field${errors.porta ? ' impressoras-page__field--error' : ''}`}>
                  <EthernetPort
                    className="impressoras-page__field-icon"
                    size={18}
                    aria-hidden="true"
                  />
                  <input
                    id="porta"
                    type="number"
                    min={1}
                    max={65535}
                    placeholder="9100"
                    {...register('porta', { valueAsNumber: true })}
                  />
                </div>
                {errors.porta?.message && (
                  <p className="impressoras-page__field-error">
                    <AlertCircle
                      className="impressoras-page__field-icon impressoras-page__field-icon--alert"
                      size={14}
                      aria-hidden="true"
                    />
                    {errors.porta.message}
                  </p>
                )}
              </div>

              {formError && (
                <p
                  className="impressoras-page__form-alert impressoras-page__form-alert--error"
                  role="alert"
                >
                  <AlertCircle
                    className="impressoras-page__field-icon impressoras-page__field-icon--alert"
                    size={14}
                    aria-hidden="true"
                  />
                  {formError}
                </p>
              )}
            </form>
          </Modal.Content>
          <Modal.Footer className="impressoras-page__modal-footer">
            <button
              type="button"
              className="impressoras-page__btn-secondary impressoras-page__modal-btn"
              disabled={mutationPending}
              onClick={closeModal}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="impressora-form"
              className="impressoras-page__btn-submit impressoras-page__modal-btn"
              disabled={mutationPending}
            >
              {editing ? 'Salvar' : 'Cadastrar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {deleting && (
        <Modal
          onClose={() => setDeleting(null)}
          className="impressoras-page__confirm-modal"
        >
          <Modal.Header title="Excluir impressora" />
          <Modal.Content>
            <p>
              Tem certeza que deseja excluir a impressora <strong>{deleting.nome}</strong>? Essa
              ação não pode ser desfeita.
            </p>
          </Modal.Content>
          <Modal.Footer className="impressoras-page__modal-footer">
            <button
              type="button"
              className="impressoras-page__btn-secondary impressoras-page__modal-btn"
              disabled={deleteMutation.isPending}
              onClick={() => setDeleting(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="impressoras-page__btn-danger impressoras-page__modal-btn"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleting.id)}
            >
              Excluir
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}